/**
 * ScopeRibbon (W1 S2b — Screen Contract G01, "Scope ribbon").
 *
 * The launch pack's Screen Contract sheet defines G01's purpose as: "Make
 * the exact read context visible and reproducible while preventing a hidden
 * DEV, first-portal, last-tab, or stale browser default from becoming an
 * action target." Concretely: this bar shows exactly what ScopeContext
 * derives from the current URL, and nothing it doesn't — no environment in
 * the URL renders an explicit "All environments" read-only indicator (the
 * docx rule referenced in the story: missing environment = all/read-only,
 * never a silent default). Changing the environment is a real router
 * navigation via ScopeContext (never a raw URL write), so Back/reload/copied
 * links all restore the scope shown here — see ScopeContext.tsx.
 *
 * A pending unsaved form on the current page blocks the change via
 * useDirtyGuard (utils/dirtyGuard.ts) rather than discarding it silently.
 *
 * Deliberately NOT mounted into Header/Layout by this task — those files
 * are S0's (components/layout/*), outside this task's file ownership. See
 * the PR body for the mount-site handoff to the Stream 7 (G01-G11 shell
 * closure) task.
 */
import React from 'react';
import { useScope } from '../../contexts/ScopeContext';
import { ENVIRONMENTS, PROVISIONED_ENVIRONMENTS, type Environment } from '../../contexts/EnvironmentContext';
import { useDirtyGuard } from '../../utils/dirtyGuard';

function isEnvironment(value: string | null): value is Environment {
  return value !== null && (ENVIRONMENTS as string[]).includes(value);
}

export const ScopeRibbon: React.FC = () => {
  const scope = useScope();
  const guardedChange = useDirtyGuard();

  const environmentId = scope.environmentId;
  const hasEnvironment = isEnvironment(environmentId);

  const onEnvironmentChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const next = e.target.value;
    guardedChange(() => scope.setEnvironmentId(next));
  };

  return (
    <div
      role="region"
      aria-label="Scope"
      data-testid="scope-ribbon"
      className="flex flex-wrap items-center gap-3 px-4 lg:px-6 py-2 border-b border-gray-200 bg-white text-sm"
    >
      <span className="font-medium text-gray-500">Scope</span>

      {hasEnvironment ? (
        <label className="flex items-center gap-1.5">
          <span className="text-gray-500">Environment</span>
          <select
            aria-label="Environment"
            value={environmentId}
            onChange={onEnvironmentChange}
            className="rounded border border-gray-300 bg-white px-2 py-1 text-iflow-900 focus:outline-none focus:ring-2 focus:ring-iflow-500"
          >
            {ENVIRONMENTS.map((env) => {
              const provisioned = PROVISIONED_ENVIRONMENTS.includes(env);
              return (
                <option key={env} value={env} disabled={!provisioned}>
                  {env.toUpperCase()}{provisioned ? '' : ' (not provisioned)'}
                </option>
              );
            })}
          </select>
        </label>
      ) : (
        <span
          data-testid="scope-ribbon-all-environments"
          title="No environment is selected in the URL — showing the estate-wide, read-only view (never a hidden default)"
          className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-semibold text-slate-600"
        >
          <span className="w-1.5 h-1.5 rounded-full bg-slate-400" aria-hidden="true" />
          All environments · read-only
        </span>
      )}

      {scope.portalId && (
        <span className="flex items-center gap-1.5 text-gray-500">
          <span>Portal</span>
          <span className="font-medium text-iflow-900">{scope.portalId}</span>
        </span>
      )}
    </div>
  );
};

export default ScopeRibbon;
